import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { Loader2, Check, AlertCircle } from 'lucide-react';

export function AutoMergeDuplicates({ onMergeComplete }: { onMergeComplete?: () => void }) {
  const [isMerging, setIsMerging] = useState(false);
  const [result, setResult] = useState<any>(null);

  const handleAutoMerge = async () => {
    setIsMerging(true);
    setResult(null);

    try {
      const { data, error } = await supabase.functions.invoke('merge-duplicate-contacts', {
        body: { action: 'merge_all' }
      });

      if (error) throw error;

      setResult(data);
      toast.success(`✅ Merge completo! ${data.processed} grupos mesclados.`);
      
      // Recarregar a lista de duplicatas após o merge
      if (onMergeComplete) {
        await onMergeComplete();
      }
    } catch (error: any) {
      console.error('Erro no merge automático:', error);
      
      // Se for timeout/fetch error, avisar que pode ter funcionado
      if (error.name === 'AbortError' || error.message?.includes('Failed to fetch')) {
        toast.warning('⏳ Operação em andamento no backend', {
          description: 'O merge pode ainda estar sendo processado. Aguarde 1-2 minutos e clique em "Atualizar" para ver os resultados.',
          duration: 10000
        });
      } else {
        toast.error('Erro ao executar merge: ' + error.message);
      }
    } finally {
      setIsMerging(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Merge Automático de Duplicados</CardTitle>
        <CardDescription>
          Executa o merge de todos os contatos duplicados usando a estratégia "keep most complete"
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <Button 
          onClick={handleAutoMerge}
          disabled={isMerging}
          size="lg"
          className="w-full"
        >
          {isMerging ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Mesclando contatos...
            </>
          ) : (
            <>
              <Check className="mr-2 h-4 w-4" />
              Executar Merge Automático
            </>
          )}
        </Button>

        {result && (
          <div className="space-y-2 p-4 bg-muted rounded-lg">
            <div className="flex items-center gap-2">
              <Check className="h-5 w-5 text-green-500" />
              <span className="font-semibold">Merge concluído!</span>
            </div>
            <div className="text-sm space-y-1">
              <p>✅ Grupos processados: <strong>{result.processed}</strong></p>
              <p>📊 Total de grupos encontrados: <strong>{result.total}</strong></p>
              {result.errors > 0 && (
                <p className="text-destructive flex items-center gap-1">
                  <AlertCircle className="h-4 w-4" />
                  Erros: <strong>{result.errors}</strong>
                </p>
              )}
            </div>

            {result.results && result.results.length > 0 && (
              <div className="mt-4">
                <p className="text-sm font-medium mb-2">Primeiros 10 resultados:</p>
                <div className="text-xs space-y-1">
                  {result.results.map((r: any, i: number) => (
                    <div key={i} className="flex justify-between">
                      <span>{r.phone}</span>
                      <span className="text-muted-foreground">{r.merged} mesclados</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
